import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useCallback, useRef, useSyncExternalStore} from 'react'

// Container ID for measuring container width to match CSS container queries
export const DIFF_COMPARISON_VIEWER_CONTAINER_ID = 'diff-comparison-viewer-container'

// Container query breakpoint that matches the CSS @container (min-width: 1012px)
export const WIDE_MODE_CONTAINER_BREAKPOINT = 1012

// Custom 1400px breakpoint — wide enough for the Overview sidebar and docked panel to coexist
export const SIDEBAR_WITH_PANEL_BREAKPOINT = 1400

/**
 * Measures the diff comparison viewer container width and returns
 * whether it meets the wide-mode threshold (>= 1012px).
 * Falls back to window.innerWidth if container has 0 width (JSDOM) or is not found.
 */
export function measureIsWideViewport(): boolean {
  if (typeof document === 'undefined') return false
  const container = document.getElementById(DIFF_COMPARISON_VIEWER_CONTAINER_ID)
  if (container) {
    const currentWidth = container.getBoundingClientRect().width
    if (currentWidth === 0 && ssrSafeWindow) {
      return ssrSafeWindow.innerWidth >= WIDE_MODE_CONTAINER_BREAKPOINT
    }
    return currentWidth >= WIDE_MODE_CONTAINER_BREAKPOINT
  }
  if (ssrSafeWindow) {
    return ssrSafeWindow.innerWidth >= WIDE_MODE_CONTAINER_BREAKPOINT
  }
  return false
}

/**
 * Checks whether a pre-measured container width meets the wide-mode threshold.
 * Use this with ResizeObserver entries to avoid forcing a synchronous layout.
 */
export function isWideWidth(containerWidth: number): boolean {
  if (containerWidth === 0 && ssrSafeWindow) {
    return ssrSafeWindow.innerWidth >= WIDE_MODE_CONTAINER_BREAKPOINT
  }
  return containerWidth >= WIDE_MODE_CONTAINER_BREAKPOINT
}

/**
 * SSR-safe hook that tracks whether the viewport currently matches `(min-width: {px}px)`.
 *
 * useMedia is not exported from @primer/react top-level, so we implement this locally.
 * useSyncExternalStore avoids hydration mismatches: the server snapshot is always false, and the
 * client reconciles to the real match state after hydration without a mismatch warning.
 */
export function useMinWidth(px: number): boolean {
  const query = `(min-width: ${px}px)`
  // Cache a single MediaQueryList per query so subscribe and getSnapshot share the same instance
  // and we don't re-run window.matchMedia on every render. Created lazily inside the callbacks,
  // which only run on the client, keeping the hook SSR-safe.
  const mqlRef = useRef<MediaQueryList | null>(null)
  const getMql = useCallback(() => {
    if (!mqlRef.current || mqlRef.current.media !== query) {
      mqlRef.current = window.matchMedia(query)
    }
    return mqlRef.current
  }, [query])
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mq = getMql()
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    },
    [getMql],
  )
  const getSnapshot = useCallback(() => getMql().matches, [getMql])
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
